import type { RadioGroupResult } from '@myosh/odin-components'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { sortBy } from 'lodash'
import type { HierarchyCriteria } from '../../API/rtk-query-api/store/tmsApi'
import type { DropdownOption } from '../../components/UI/multi-select-dropdown/MultiSelectDropdown'
import type { ExtendedDropdownResult } from '../types/hierarchy-values'

export interface GAViewFilters {
  competencies: Array<DropdownOption>
  people: Array<DropdownOption>
  managers: Array<DropdownOption>
  occupations: Array<DropdownOption>
  hierarchies: Array<HierarchyCriteria>
  hierarchyViewMode: RadioGroupResult
  showAllReports: boolean
  showGridSeparator: boolean
  gridlinesFrequency: number
  showManagersColumn: boolean
  columns: Array<ExtendedDropdownResult>
}

export const fieldNames = {
  PERSON: 'Person',
  OCCUPATION: 'Occupation',
  MANAGERS: 'Manager',
  SECONDARY_OCCUPATION: 'Secondary Occupation(s)',
  AFFILIATION: 'Affiliation',
  REQUIRED_TRAINING: 'Required',
  IN_TRAINING: 'In Training',
  COMPLETED_TRAINING: 'Completed',
  OUTSTANDING: 'Outstanding'
}

const allColumns: Array<ExtendedDropdownResult> = [
  { weight: -1, value: fieldNames.SECONDARY_OCCUPATION, text: fieldNames.SECONDARY_OCCUPATION },
  { weight: 2, value: fieldNames.AFFILIATION, text: fieldNames.AFFILIATION },
  { weight: 3, value: fieldNames.REQUIRED_TRAINING, text: fieldNames.REQUIRED_TRAINING },
  //"In training" with weight: 4, will be added later
  { weight: 5, value: fieldNames.COMPLETED_TRAINING, text: fieldNames.COMPLETED_TRAINING },
  { weight: 6, value: fieldNames.OUTSTANDING, text: fieldNames.OUTSTANDING }
]

interface GapAnalysisFilterState {
  showGridSeparator: boolean
  gridlinesFrequency: number
  selectedManagers: Array<DropdownOption>
  selectedCompetencies: Array<DropdownOption>
  showAllReports: boolean
  selectedPeople: Array<DropdownOption>
  selectedOccupations: Array<DropdownOption>
  hierarchyViewMode: RadioGroupResult
  selectedHierarchies: Array<HierarchyCriteria>
  showNoPermissionToFetchHierarchies: boolean
  showManagersColumn: boolean
  columns: Array<ExtendedDropdownResult>
  selectedColumns: Array<ExtendedDropdownResult>
}

const initialState: GapAnalysisFilterState = {
  // Display tab filters
  showGridSeparator: true,
  gridlinesFrequency: 1,
  // Filter tab
  selectedManagers: [],
  selectedCompetencies: [],
  showAllReports: false,
  //
  selectedPeople: [],
  selectedOccupations: [],
  // Hierarchy filtering
  hierarchyViewMode: { text: 'All', value: 'all' },
  selectedHierarchies: [],
  showNoPermissionToFetchHierarchies: false,

  //columns
  showManagersColumn: false,
  columns: allColumns,
  selectedColumns: []
}

const gapAnalysisFiltersSlice = createSlice({
  name: 'gapAnalysisFilters',
  initialState,
  reducers: {
    setShowGridSeparator(state, action: PayloadAction<boolean>) {
      state.showGridSeparator = action.payload
    },
    setGridlinesFrequency(state, action: PayloadAction<number>) {
      state.gridlinesFrequency = action.payload
    },
    setSelectedCompetences(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedCompetencies = action.payload
    },
    setSelectedManagers(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedManagers = action.payload
    },
    setShowAllReports(state, action: PayloadAction<boolean>) {
      state.showAllReports = action.payload
    },
    setSelectedHierarchyViewMode(state, action: PayloadAction<RadioGroupResult>) {
      state.hierarchyViewMode = action.payload
    },
    setSelectedHierarchies(state, action: PayloadAction<Array<HierarchyCriteria>>) {
      state.selectedHierarchies = action.payload
    },
    setSelectedPeople(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedPeople = action.payload
    },
    setSelectedOccupations(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedOccupations = action.payload
    },
    setShowManagersColumn(state, action: PayloadAction<boolean>) {
      state.showManagersColumn = action.payload
    },
    setColumns(state, action: PayloadAction<Array<ExtendedDropdownResult>>) {
      state.columns = sortBy(action.payload, column => column.weight)
    },
    setSelectedColumns(state, action: PayloadAction<Array<ExtendedDropdownResult>>) {
      state.selectedColumns = sortBy(action.payload, column => column.weight)
    },
    setViewFilters(state, action: PayloadAction<GAViewFilters>) {
      state.selectedCompetencies = action.payload.competencies
      state.selectedPeople = action.payload.people
      state.selectedManagers = action.payload.managers
      state.selectedOccupations = action.payload.occupations
      state.selectedHierarchies = action.payload.hierarchies
      state.hierarchyViewMode = action.payload.hierarchyViewMode
      state.showAllReports = action.payload.showAllReports
      state.showGridSeparator = action.payload.showGridSeparator
      state.gridlinesFrequency = action.payload.gridlinesFrequency
      state.showManagersColumn = action.payload.showManagersColumn
      state.selectedColumns = action.payload.columns
    }
  }
})

export const gapAnalysisFiltersReducer = gapAnalysisFiltersSlice.reducer
export const gapAnalysisFiltersActions = {
  ...gapAnalysisFiltersSlice.actions
}
